import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Activity, MessageSquare, Clock, User, ChevronRight, ArrowLeft, Eye } from 'lucide-react';

export default function TrackingPage() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    useEffect(() => {
        api.get('/tracking/sessions').then(({ data }) => setSessions(data)).catch(() => { }).finally(() => setLoading(false));
    }, []);

    const viewSession = async (session) => {
        setSelectedSession(session);
        setLoadingMessages(true);
        try {
            const { data } = await api.get(`/tracking/sessions/${session.id}`);
            setMessages(data.messages || []);
        } catch (err) {
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDuration = (start, end) => {
        if (!start) return '—';
        const s = new Date(start);
        const e = end ? new Date(end) : new Date();
        const diffMs = e - s;
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return '< 1 min';
        if (mins < 60) return `${mins} min`;
        const hrs = Math.floor(mins / 60);
        return `${hrs}h ${mins % 60}min`;
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    // Session detail view
    if (selectedSession) {
        return (
            <div>
                <button onClick={() => { setSelectedSession(null); setMessages([]); }}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Retour aux sessions
                </button>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Session de {selectedSession.recruiter_name || 'Visiteur inconnu'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {selectedSession.recruiter_company && `${selectedSession.recruiter_company} • `}
                                {formatDate(selectedSession.started_at)}
                                {' • '}
                                Durée : {formatDuration(selectedSession.started_at, selectedSession.ended_at)}
                            </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${selectedSession.is_active
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                            }`}>
                            {selectedSession.is_active ? '● Active' : 'Terminée'}
                        </span>
                    </div>
                </div>

                {/* Conversation */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="font-medium text-gray-900">
                            Conversation ({messages.length} messages)
                        </h3>
                    </div>

                    {loadingMessages ? (
                        <div className="p-8 text-center text-gray-400">Chargement...</div>
                    ) : messages.length === 0 ? (
                        <div className="p-8 text-center text-gray-400">Aucun message</div>
                    ) : (
                        <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                            {messages.map((msg, i) => (
                                <div key={msg.id || i} className={`px-6 py-4 ${msg.role === 'user' ? 'bg-blue-50/30' : ''}`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${msg.role === 'user'
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-indigo-100 text-indigo-700'
                                            }`}>
                                            {msg.role === 'user' ? 'R' : 'IA'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-medium text-gray-700">
                                                    {msg.role === 'user' ? 'Recruteur' : 'Claud-IA'}
                                                </span>
                                                <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                                            </div>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Sessions list
    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Suivi des sessions</h1>
                <p className="text-sm text-gray-500 mt-1">
                    Suivez les conversations entre l'IA et les recruteurs
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <Activity className="w-5 h-5 text-[#5764F4]" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
                        <p className="text-xs text-gray-500">Sessions totales</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">
                            {sessions.filter(s => s.is_active).length}
                        </p>
                        <p className="text-xs text-gray-500">Actives</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">
                            {sessions.reduce((sum, s) => sum + (s.message_count || 0), 0)}
                        </p>
                        <p className="text-xs text-gray-500">Messages totaux</p>
                    </div>
                </div>
            </div>

            {/* Sessions list */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {sessions.length === 0 ? (
                    <div className="text-center py-16 px-4">
                        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">Aucune session</p>
                        <p className="text-gray-400 text-sm mt-1">
                            Les sessions apparaîtront ici quand les recruteurs utiliseront vos liens
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {sessions.map((s) => (
                            <button key={s.id} onClick={() => viewSession(s)}
                                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${s.is_active ? 'bg-green-50' : 'bg-gray-50'
                                        }`}>
                                        <User className={`w-5 h-5 ${s.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {s.recruiter_name || 'Visiteur anonyme'}
                                        </p>
                                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                                            {s.recruiter_company && <span>{s.recruiter_company}</span>}
                                            <span>{formatDate(s.started_at)}</span>
                                            <span>{s.message_count || 0} msg</span>
                                            <span>{formatDuration(s.started_at, s.ended_at)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    {s.is_active && (
                                        <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                                    )}
                                    <ChevronRight className="w-4 h-4 text-gray-300" />
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
